import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";
import ContactChoice from "@/components/fields/ContactChoice";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import Skeleton from "react-loading-skeleton";
import { useQueryClient } from "@tanstack/react-query";
import {
  DASHBOARD_QUERY_GC_TIME,
  DASHBOARD_QUERY_STALE_TIMES,
  dashboardQueryKeys,
} from "@/dashboard/dashboardQueryConfig";

const Representants = ({ open, setOpen, funder }) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation("dashboard", {
    keyPrefix: "financeur",
  });

  const { fetchData } = useFetchDataV2<any>("", {
    browserCache: true,
    browserCacheKey: "funder-representants",
    browserCacheNamespace: "dashboard",
    browserCacheTtlMs: 1000 * 60 * 60,
    gcTime: DASHBOARD_QUERY_GC_TIME,
    staleTime: DASHBOARD_QUERY_STALE_TIMES.funderRepresentants,
  });
  const [representants, setRepresentants] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [metaCarrier, setMetaCarrier] = useState<number | null>(null);

  const updateFunderRepresentants = (nextRepresentants: any[]) => {
    setRepresentants(nextRepresentants);

    if (!funder?.id) return;
    const representantsEndpoint = `${import.meta.env.VITE_API_ENDPOINT}/representants/${funder.id}`;
    const representantsCacheKey = dashboardQueryKeys.funderRepresentants(
      funder.id,
    );

    queryClient.setQueriesData(
      {
        exact: false,
        queryKey: [dashboardQueryKeys.funders],
      },
      (current: any[] | undefined) =>
        Array.isArray(current)
          ? current.map((currentFunder) =>
              String(currentFunder.id) === String(funder.id)
                ? { ...currentFunder, representants: nextRepresentants }
                : currentFunder,
            )
          : current,
    );
    queryClient.setQueryData(representantsCacheKey, {
      representants: nextRepresentants,
      metaCarrier,
    });
    queryClient.setQueryData(
      ["fetchDataV2", representantsEndpoint],
      {
        representants: nextRepresentants,
        metaCarrier,
      },
    );
  };

  useEffect(() => {
    if (!open || funder === null) {
      setLoading(false);
      return;
    }

    let ignore = false;
    const cachedRepresentants = Array.isArray(funder?.representants)
      ? funder.representants
      : [];

    setRepresentants(cachedRepresentants);
    setLoading(cachedRepresentants.length === 0);

    const representantsEndpoint = `${import.meta.env.VITE_API_ENDPOINT}/representants/${funder?.id}`;
    fetchData(
      representantsEndpoint,
      {
        gcTime: DASHBOARD_QUERY_GC_TIME,
        queryKey: dashboardQueryKeys.funderRepresentants(funder.id),
        staleTime: DASHBOARD_QUERY_STALE_TIMES.funderRepresentants,
      },
    ).then((res) => {
      if (ignore) return;
      //   console.log(
      //     "representants2",
      //     Array.isArray(res) ? res : [Object.values(res as any)[0]]
      //   );
      const fetchedRepresentants = Array.isArray(res?.representants)
        ? res.representants
        : [];

      setRepresentants((currentRepresentants) =>
        fetchedRepresentants.length === 0 && currentRepresentants.length > 0
          ? currentRepresentants
          : fetchedRepresentants,
      );
      setMetaCarrier(res?.metaCarrier ?? null);
      setLoading(false);
    });

    return () => {
      ignore = true;
    };
  }, [open, funder?.id]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="sm:max-h-[80vh] sm:max-w-[50vw] overflow-y-auto"
        id="addFinancementContent"
      >
        <DialogHeader className="">
          <DialogTitle asChild>
            <h2 className="mb-2 mt-5 flex gap-4 text-center text-xl font-bold">
              {t("Liste des representants de")} {funder?.fullname}
            </h2>
          </DialogTitle>
          <DialogDescription asChild className="text-[#595959]">
            <p className="">
              {t("Retrouvez ici la liste des representants de")}{" "}
              {funder?.fullname}.
            </p>
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap my-5">
          {loading && <LoadingCmp />}
          {!loading && (
            <ContactChoice
              financer={funder}
              className="mt-2"
              contacts={representants}
              metaCarrier={metaCarrier}
              selected={null}
              handleChange={(value) => null}
              withActions={true}
              onContactsChange={updateFunderRepresentants}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ContactSkeleton = () => (
  <div className="cursor-pointer bg-white shadow-md rounded-2xl p-4 w-full max-w-md mx-auto">
    <h2 className="text-xl font-semibold text-gray-800">
      <Skeleton width={120} />
    </h2>
    <p className="text-gray-600 mt-2">
      📞 <Skeleton width={100} />
    </p>
    <p className="text-gray-600 mt-1">
      📧 <Skeleton width={160} />
    </p>
  </div>
);

const LoadingCmp = () => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      <ContactSkeleton />
      <ContactSkeleton />
      <ContactSkeleton />
    </div>
  );
};

export default Representants;
